import React, { useState, useEffect } from 'react';
import { getAchievements, getCertifications, getMemberships } from '../services/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Award, GraduationCap, Users2, ExternalLink, Linkedin } from 'lucide-react';

const Achievements = () => {
  const bgColor = '#ffffff';
  const textColor = '#1a2332';
  const accentColor = '#d4af37';
  
  const [achievements, setAchievements] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [achievementsData, certificationsData, membershipsData] = await Promise.all([
          getAchievements(),
          getCertifications(),
          getMemberships()
        ]);
        setAchievements(achievementsData);
        setCertifications(certificationsData);
        setMemberships(membershipsData);
      } catch (error) {
        console.error('Error loading achievements data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <section id="achievements" className="py-20" style={{ backgroundColor: bgColor, color: textColor }}>
      <div className="container mx-auto px-6">
        <div className="mb-16 text-center">
          <h2 className="mb-4 font-serif text-4xl font-bold md:text-5xl">Recognition & Credentials</h2>
          <div className="mx-auto h-1 w-24 rounded" style={{ backgroundColor: accentColor }}></div>
          <p className="mx-auto mt-6 max-w-3xl text-lg opacity-90">
            Industry recognition and professional certifications that demonstrate commitment to excellence
          </p>
        </div>

        <Tabs defaultValue="awards" className="mx-auto max-w-6xl">
          <TabsList className="mx-auto mb-8 grid w-full max-w-3xl grid-cols-3 gap-4 bg-transparent">
            <TabsTrigger
              value="awards"
              className="rounded-lg border-2 px-4 py-3 font-medium transition-all"
              style={{
                borderColor: accentColor,
                color: textColor
              }}
            >
              <Award className="mr-2 inline h-5 w-5" />
              Awards
            </TabsTrigger>
            <TabsTrigger
              value="certifications"
              className="rounded-lg border-2 px-4 py-3 font-medium transition-all"
              style={{
                borderColor: accentColor,
                color: textColor
              }}
            >
              <GraduationCap className="mr-2 inline h-5 w-5" />
              Certifications
            </TabsTrigger>
            <TabsTrigger
              value="memberships"
              className="rounded-lg border-2 px-4 py-3 font-medium transition-all"
              style={{
                borderColor: accentColor,
                color: textColor
              }}
            >
              <Users2 className="mr-2 inline h-5 w-5" />
              Memberships
            </TabsTrigger>
          </TabsList>

          <TabsContent value="awards">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {achievements.map((achievement) => (
                <Card
                  key={achievement.id}
                  className="group border-none shadow-lg transition-all hover:scale-105"
                >
                  <CardContent className="p-6">
                    <div
                      className="mb-4 inline-flex rounded-full p-3 transition-all group-hover:scale-110"
                      style={{ backgroundColor: `${accentColor}20` }}
                    >
                      <Award className="h-8 w-8" style={{ color: accentColor }} />
                    </div>
                    <Badge
                      className="mb-3 border-none px-2 py-1 text-xs"
                      style={{ backgroundColor: `${accentColor}30`, color: textColor }}
                    >
                      {achievement.category}
                    </Badge>
                    <h3 className="mb-2 text-lg font-bold leading-tight">{achievement.title}</h3>
                    <p className="mb-1 text-sm font-medium opacity-70">{achievement.organization}</p>
                    <p className="text-sm font-semibold" style={{ color: accentColor }}>
                      {achievement.year}
                    </p>
                    {achievement.note && (
                      <p className="mt-2 text-xs italic opacity-60">{achievement.note}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="certifications">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {certifications.map((cert) => (
                <Card
                  key={cert.id}
                  className="group border-none shadow-lg transition-all hover:scale-105"
                >
                  <CardContent className="p-6">
                    <div
                      className="mb-4 inline-flex rounded-full p-3 transition-all group-hover:scale-110"
                      style={{ backgroundColor: `${accentColor}20` }}
                    >
                      <GraduationCap className="h-8 w-8" style={{ color: accentColor }} />
                    </div>
                    <h3 className="mb-2 text-lg font-bold leading-tight">{cert.name}</h3>
                    <p className="mb-1 text-sm font-medium opacity-70">{cert.issuer}</p>
                    <p className="text-sm font-semibold" style={{ color: accentColor }}>
                      {cert.year}
                    </p>
                    {cert.note && (
                      <p className="mt-2 text-xs italic opacity-60">{cert.note}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="memberships">
            <div className="grid gap-6 md:grid-cols-2">
              {memberships.map((membership) => (
                <Card
                  key={membership.id}
                  className="group border-none shadow-lg transition-all hover:scale-105"
                >
                  <CardContent className="p-6">
                    <div className="mb-4 flex items-start justify-between">
                      <div
                        className="inline-flex rounded-full p-3 transition-all group-hover:scale-110"
                        style={{ backgroundColor: `${accentColor}20` }}
                      >
                        <Users2 className="h-8 w-8" style={{ color: accentColor }} />
                      </div>
                      <Badge
                        className="border-none px-3 py-1 text-xs font-semibold"
                        style={{ backgroundColor: '#22c55e', color: 'white' }}
                      >
                        {membership.status}
                      </Badge>
                    </div>
                    
                    <h3 className="mb-2 text-xl font-bold leading-tight">{membership.organization}</h3>
                    <p className="mb-1 text-base font-medium" style={{ color: accentColor }}>
                      {membership.role}
                    </p>
                    <p className="mb-4 text-sm opacity-70">{membership.period}</p>
                    
                    <div className="flex flex-wrap gap-3">
                      <a
                        href={membership.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium transition-all hover:underline"
                        style={{ color: accentColor }}
                      >
                        <ExternalLink className="h-4 w-4" />
                        Website
                      </a>
                      <a
                        href={membership.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm font-medium transition-all hover:underline"
                        style={{ color: accentColor }}
                      >
                        <Linkedin className="h-4 w-4" />
                        LinkedIn
                      </a>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default Achievements;
